import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { I18nextProvider } from 'react-i18next';
import r2wc from "react-to-webcomponent"

import i18n from './i18n';
import reportWebVitals from './reportWebVitals';
import { generateUUID, loadScript, loadStyleSheet } from './util/util';

import App from './App';
import './index.css';

// Import Context Providers
import { PlayerProvider } from './contexts/PlayerContext';
import { ConfigurationProvider } from './contexts/ConfigurationContext';
import { ChatLoginProvider } from './contexts/ChatLoginContext';
import { PresenterProvider } from './contexts/PresenterContext';
import { ShoppingProvider } from './contexts/ShoppingContext';
import { RestrictionProvider } from './contexts/RestrictionContext';
import ModalPlayerPoster from './components/Poster/ModalPlayerPoster';
import WidgetComponent from './components/widgets/WidgetComponent';
import QplayerAppReels from './components/StoryFeed/QplayerAppReels';
import { registerReelsApi } from './components/StoryFeed/reels-api';

const StrictMode = process.env.NODE_ENV === 'development' ? React.StrictMode : React.Fragment;

const QplayerApp = (props) => {
	const [isGoBrainLoaded, setIsGoBrainLoaded] = useState(false);
	const [isCssLoaded, setIsCssLoaded] = useState(false);

	const { container, qplayerModalInstance } = props;
	const containerKey =  useMemo(() => {
		const containerKeyAttr = container?.getAttribute('data-qplayer-key');
		return containerKeyAttr || (container ? `${container?.dataset?.qplayerId || generateUUID()}` : undefined)
	}, [container]);

	useEffect(() => {
		// Inject GoBrain script into header
		if (process.env.REACT_APP_GOBRAIN_URL) {
			if (container?.dataset?.qplayerPreview === 'true') {
				setIsGoBrainLoaded(true);
			} else {
				loadScript({ src: process.env.REACT_APP_GOBRAIN_URL, onload: () => setIsGoBrainLoaded(true) });
			}
		}

		// Inject QPlayer CSS into header
		if (process.env.PUBLIC_URL) {
			loadStyleSheet({ href: `${process.env.PUBLIC_URL}/qplayer.min.css`, onload: () => setIsCssLoaded(true), })
		}

		if (container?.dataset?.qplayerPreview !== 'true') {
			loadScript({ src: `https://play2.qbrick.com/md5/md5.js` });
		}
		container && !container?.getAttribute('data-qplayer-key') && container.setAttribute('data-qplayer-key', containerKey);
	}, [containerKey, container]);

	return isGoBrainLoaded && isCssLoaded ? <StrictMode>
		<PlayerProvider>
			<ConfigurationProvider>
				<ChatLoginProvider>
					<PresenterProvider>
						<ShoppingProvider>
							<RestrictionProvider>
								<I18nextProvider i18n={i18n}>
									<App {...container?.dataset} qplayerModalInstance={qplayerModalInstance} container={container} containerKey={containerKey} />
								</I18nextProvider>
							</RestrictionProvider>
						</ShoppingProvider>
					</PresenterProvider>
				</ChatLoginProvider>
			</ConfigurationProvider>
		</PlayerProvider>
	</StrictMode> : <></>;
};

const QplayerAppModal = (props) => {
	const { container } = props;
	const modalKey = useMemo(() => {
		return container?.dataset?.qplayerId ? `${container?.dataset?.qplayerId}-modal` : `${generateUUID()}-modal`;
	}, [container]);

	useEffect(() => {
		setTimeout(() => {
			const player = document.createElement('qplayer-embed');
			Object.keys(container?.dataset || {}).forEach(key => {
				if (key.startsWith('qplayer')) {
					const attrKey = 'data-' + key.replace(/([A-Z])/g, '-$1').toLowerCase();
					player.setAttribute(attrKey, container?.dataset[key]);
				}
			});

			player.setAttribute('data-qplayer-inline', "off" );
			player.setAttribute('data-qplayer-autoplay', "off");
			player.setAttribute('data-qplayer-key', modalKey);
			player.setAttribute('data-qplayer-modal-instance', "true");
			player.classList.add('qplayer-modal-player-container');

			container && container.setAttribute('data-qplayer-modal-embed-key', modalKey);
			document.body.appendChild(player);
		}, 1000);
	}, [container, modalKey]);

	return <StrictMode>
		<ModalPlayerPoster {...container?.dataset} container={container} modalKey={modalKey} />
	</StrictMode>;
};

const QplayerAppWidget = (props) => {
	const { container } = props;
    const widgetKey = useMemo(() => {
		return container?.dataset?.qplayerWidgetScenarioId ? `${container?.dataset?.qplayerWidgetScenarioId}-widget` : `${generateUUID()}-widget`;
	}, [container]);

	return <StrictMode>
		<WidgetComponent {...container?.dataset} container={container} widgetKey={widgetKey}/>
	</StrictMode>;
};

customElements.define("qplayer-embed", r2wc(QplayerApp, React, ReactDOM));
customElements.define("qplayer-modal-embed", r2wc(QplayerAppModal, React, ReactDOM));
customElements.define("qplayer-widget-embed", r2wc(QplayerAppWidget, React, ReactDOM));
customElements.define("qplayer-reels-embed", r2wc(QplayerAppReels, React, ReactDOM));

// window.QplayerReels.openReels / closeReels. A façade over the element's own methods, named and
// addressed like window.QplayerLoader.
registerReelsApi();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();